/* eslint-disable react/display-name */
import MaterialTable from '@material-table/core'
import ClearSharpIcon from '@mui/icons-material/ClearSharp'
import DvrSharpIcon from '@mui/icons-material/DvrSharp'
import { Box, Button, Paper } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useUserRole from 'hooks/useUserRole'
import React from 'react'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import { Client } from '../../../api/client.types'
import { clientsAPI } from '../../../api/clientsAPI'
import { BaseLayout } from '../../../layouts/Base/BaseLayout'
import styles from './ClientSearchPageStyle'

interface ClientRowData {
  clientId: number | undefined
  accountNumber: string
  companyName: string
  registrationNumber: string
  accountManager: string
  status: string
}

const mapToRowData = (client: Client): ClientRowData => {
  const data = {
    clientId: client.id,
    accountNumber: client.accountNumber ? client.accountNumber : '',
    companyName: client.companyName ? client.companyName : '',
    tradingAs: client.alias ? client.alias : '',
    registrationNumber: client.registrationNumber
      ? client.registrationNumber
      : '',
    accountManager: client.accountManager ? client.accountManager : '',
    status: client.status ? client.status : '',
  }
  return data
}

const getTableData = (clients: Array<Client>) => {
  const data: Array<object | JSX.Element> = []
  if (clients) {
    clients.forEach((client) => {
      const mapped = mapToRowData(client)
      data.push(mapped)
    })
  }
  return data
}

const ClientSearchPage = () => {
  const { validateAccess } = useUserRole()
  const theme = useTheme() as any
  const headerBackgroundColor =
    theme.components?.journeyStatus?.headerColour ?? theme.palette.grey[300]
  const headerTextColor =
    theme.components?.journeyStatus?.headerTextColour ??
    theme.palette.getContrastText(headerBackgroundColor)

  const history = useHistory()

  const { isLoading, data } = useQuery({
    queryKey: ['fetchClients'],
    queryFn: async () => {
      if (validateAccess('Jyrney_admin')) {
        return await clientsAPI.getClients()
      } else {
        return await clientsAPI.searchClients()
      }
    },
  })

  const tableData = getTableData(data?.data.value)

  const handleEditClick = (id: number) => {
    if (id) {
      history.push(`/admin/clients/${id}`)
    }
  }

  const { mutateAsync: mutate } = useMutation({
    mutationFn: (id: number) => {
      return clientsAPI.deleteClient(id)
    },
  })

  const handleDeleteClick = (
    event: React.BaseSyntheticEvent,
    clientId: number | undefined,
  ) => {
    if (clientId) {
      event.preventDefault()
      mutate(clientId).then(() => {
        history.push(`/admin/clients/search`)
      })
    }
  }

  const handleClickOpenCancel = async (rowData: ClientRowData) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <Paper elevation={3} sx={styles.customCancelBookingContainer}>
            <Box component={'h1'} sx={styles.customCancelBookingHeader}>
              Delete Confirmation
            </Box>
            <Box component={'p'} sx={styles.customCancelBookingMessage}>
              Are you sure you want to delete this client:{' '}
              {rowData.registrationNumber} - {rowData.companyName}?
            </Box>
            <div style={{ textAlign: 'right' }}>
              <Button
                sx={styles.customCancelBookingCancelButton}
                onClick={onClose}
              >
                No
              </Button>
              <Button
                color="primary"
                sx={styles.customCancelBookingConfirmButton}
                onClick={(e: React.BaseSyntheticEvent) => {
                  handleDeleteClick(e, rowData.clientId)
                  onClose()
                }}
              >
                Yes, Delete it!
              </Button>
            </div>
          </Paper>
        )
      },
    })
  }

  return (
    <BaseLayout
      pageTitle="Client Management"
      pageSubtitle="Your company details"
      mainElement={
        <MaterialTable
          title="Clients"
          columns={[
            { title: 'clientId', field: 'clientId', hidden: true },
            {
              title: 'Account Number',
              field: 'accountNumber',
              searchable: true,
            },
            {
              title: 'Trading Name',
              field: 'tradingAs',
              searchable: true,
              hidden: true,
            },
            {
              title: 'Company',
              grouping: false,
              field: 'companyName',
              searchable: true,
              render: (rowData: any) =>
                rowData.tradingAs ? (
                  <>
                    {rowData.tradingAs}{' '}
                    <span style={{ color: 'GrayText' }}>
                      ({rowData.companyName})
                    </span>
                  </>
                ) : (
                  <span>{rowData.companyName}</span>
                ),
              cellStyle: { width: 1000, maxWidth: 1000 },
              headerStyle: { width: 1000, maxWidth: 1000 },
              defaultSort: 'asc',
            },

            {
              title: 'Registration Number',
              field: 'registrationNumber',
            },
            { title: 'Status', field: 'status' },
          ]}
          options={{
            headerStyle: {
              backgroundColor: headerBackgroundColor,
              color: headerTextColor,
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
            },
            rowStyle: {
              backgroundColor: '#ffffff',
              fontSize: 'var(--fs-12, 0.75rem)',
            },
            emptyRowsWhenPaging: false,
            pageSize: 10,
            pageSizeOptions: [10, 50, 100],
            actionsColumnIndex: -1,
          }}
          localization={{
            pagination: {
              labelRowsPerPage: '',
            },
          }}
          style={{
            backgroundColor: '#ededed',
            width: '100%',
            // wordWrap: false
          }}
          actions={[
            {
              icon: () => <DvrSharpIcon fontSize="small" color="action" />,
              tooltip: 'View Client',
              onClick: (event: React.BaseSyntheticEvent, rowData: any) =>
                handleEditClick(rowData.clientId),
            },
            {
              icon: () => <ClearSharpIcon fontSize="small" color="error" />,
              tooltip: 'Delete Client',
              onClick: (event: React.BaseSyntheticEvent, rowData: any) =>
                handleClickOpenCancel(rowData),
            },
          ]}
          data={tableData}
          isLoading={isLoading}
        />
      }
    />
  )
}
export default ClientSearchPage
